// Bits the Previews pages and the lead page share.

import { useState, type FormEvent } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { Preview, PreviewStatus } from '@/api/types'
import { Badge, Button, Dialog, ExternalLinkIcon, FormField, InlineAlert, SelectInput, TextInput, useToast, type BadgeTone } from '@/components/ui'
import { daysFrom, LIFE_OPTIONS, PREVIEW_STATUS_LABELS } from '@/lib/previews'

const TONE: Record<PreviewStatus, BadgeTone> = { active: 'good', expired: 'neutral', removed: 'outline', claimed: 'accent' }

export function PreviewStatusBadge({ preview }: { preview: Pick<Preview, 'status' | 'removed_via'> }) {
  return <Badge tone={TONE[preview.status]}>{PREVIEW_STATUS_LABELS[preview.status]}</Badge>
}

/**
 * Open: a 15-minute super-admin look at the private site (the impersonation
 * flow, landing on the site), without the lead's link and without counting as
 * their visit.
 */
export function OpenPreviewButton({ preview, size = 'md' }: { preview: Pick<Preview, 'id' | 'name'>; size?: 'sm' | 'md' }) {
  const toast = useToast()
  const [pending, setPending] = useState(false)
  const open = async () => {
    // Open the tab inside the click so pop-up blockers allow it.
    const win = window.open('about:blank', '_blank')
    if (win) win.opener = null
    setPending(true)
    try {
      const { url } = await api.growth.previews.open(preview.id)
      if (win) win.location.href = url
      else window.location.assign(url)
    } catch (err) {
      win?.close()
      toast.error(`Couldn't open the preview: ${errorMessage(err)}`)
    } finally {
      setPending(false)
    }
  }
  return (
    <Button size={size} onClick={() => void open()} loading={pending} icon={<ExternalLinkIcon size={14} />} title={`Look at ${preview.name}'s site as Ops`}>
      Open
    </Button>
  )
}

export function LifeField({ label, choice, custom, onChoice, onCustom }: { label: string; choice: string; custom: string; onChoice: (v: string) => void; onCustom: (v: string) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FormField label={label}>{({ id }) => <SelectInput id={id} value={choice} options={LIFE_OPTIONS} onChange={(e) => onChoice(e.target.value)} />}</FormField>
      {choice === 'custom' ? (
        <FormField label="Days" hint="1 to 365">
          {({ id }) => <TextInput id={id} inputMode="numeric" value={custom} onChange={(e) => onCustom(e.target.value)} />}
        </FormField>
      ) : null}
    </div>
  )
}

/** Asks how long, for Extend. */
export function DaysDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  defaultDays = '30',
  warning,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  title: string
  description: string
  confirmLabel: string
  defaultDays?: string
  warning?: string
  onConfirm: (days: number) => Promise<void>
}) {
  const [choice, setChoice] = useState(defaultDays)
  const [custom, setCustom] = useState('')
  const [pending, setPending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const days = daysFrom(choice, custom)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (days === null) return
    setPending(true)
    setProblem(null)
    try {
      await onConfirm(days)
      onClose()
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} description={description}>
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        {warning ? <InlineAlert tone="warn">{warning}</InlineAlert> : null}
        <LifeField label="For how long" choice={choice} custom={custom} onChoice={setChoice} onCustom={setCustom} />
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={days === null} loading={pending}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
